import {
  Input,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@parcel/ui';
import { AppLogo } from '@parcel/ui/app-logo';
import { Search } from 'lucide-react';
import * as React from 'react';
import type { ApiContactSummary } from '../../lib/contacts.js';
import {
  contactsCountShown,
  personasPhrase,
  shownEmail,
  useAccountContactsWithReach,
} from '../../lib/enrich.js';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { ContactAvatar } from '../contacts/contact-avatar.js';
import {
  type PinnedColumn,
  getPinnedColumnClassName,
  getPinnedColumnStyle,
  getPinnedHeaderClassName,
} from '../data-table/data-table-layout.js';
import { useEnrichScope } from '../enrich/enrich-scope.js';
import { PersonaTag } from '../enrich/persona-tag.js';
import { EmailCell, PhoneCell } from '../enrich/reach-cell.js';
import { ListSkeleton } from '../entity-drawer/detail-skeletons.js';
import { EmptyTabState } from '../entity-drawer/entity-drawer-sections.js';
import { FindPeopleButton } from './find-people-dialog.js';

// Name stays put while the rest scrolls, as in the main tables; widths fit each reveal button.
const PINNED: PinnedColumn[] = [{ id: 'name', left: 0, width: 260, last: true }];
const COLUMNS = [
  { id: 'name', label: 'Name', width: 260 },
  { id: 'persona', label: 'Persona', width: 170 },
  { id: 'title', label: 'Title', width: 200 },
  { id: 'email', label: 'Email', width: 240 },
  { id: 'phone', label: 'Phone', width: 180 },
] as const;
const TABLE_MIN_WIDTH = COLUMNS.reduce((sum, column) => sum + column.width, 0);

export function AccountContactsTab({
  accountId,
  accountName,
  accountWebsite,
  onOpenContact,
  excludeContactId,
  scrollable = false,
  variant = 'account',
}: {
  accountId: string;
  accountName: string;
  accountWebsite: string | null;
  onOpenContact: (contactId: string) => void;
  /** The person whose colleagues these are, left out of the list. */
  excludeContactId?: string;
  /** Caps the table's height and scrolls inside it, for a section among others. */
  scrollable?: boolean;
  /** A contact's colleagues: titled Colleagues, with the firm under it. */
  variant?: 'account' | 'colleagues';
}) {
  const { workspaceId, status } = useEnrichScope();
  const [includeAgency, setIncludeAgency] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const contactsQuery = useAccountContactsWithReach(accountId, workspaceId, includeAgency);
  const data = contactsQuery.data;

  if (contactsQuery.isError && !data) return <EmptyTabState message="Unable to load contacts" />;
  if (!data) return <ListSkeleton />;

  const personas = data.personas ?? [];
  const listed = excludeContactId
    ? data.contacts.filter((contact) => contact.id !== excludeContactId)
    : data.contacts;
  const needle = query.trim().toLowerCase();
  const contacts = needle
    ? listed.filter((contact) =>
        [contact.name, contact.title, shownEmail(contact).value].some((value) =>
          value?.toLowerCase().includes(needle),
        ),
      )
    : listed;
  const agencyCount = data.agency_staff_count ?? 0;
  const searchPrice = status?.enabled ? status.prices.people_search_result : null;

  return (
    <div className="space-y-3">
      <div className="min-w-0 space-y-1">
        <h3 className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-foreground">
          {variant === 'colleagues' ? 'Colleagues' : `People at ${accountName}`}
          {variant === 'colleagues' ? (
            <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {contactsCountShown(listed.length, data.contacts.length)}
            </span>
          ) : null}
        </h3>
        <p className="text-xs text-muted-foreground">
          {variant === 'colleagues'
            ? `Other people at ${accountName}`
            : personas.length > 0
              ? `Matched to the ${personasPhrase(personas)} personas for this firm.`
              : 'Everyone Parcel knows at this firm.'}
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          {listed.length > 0 ? (
            <div className="relative">
              <Search
                className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden
              />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search name, title or email"
                aria-label="Search people"
                className="h-8 w-60 pl-8 text-sm"
              />
            </div>
          ) : null}
          {agencyCount > 0 ? (
            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <Switch
                checked={includeAgency}
                onCheckedChange={setIncludeAgency}
                aria-label={`Show agency staff (${agencyCount})`}
              />
              <span aria-hidden>{`Show agency staff (${agencyCount})`}</span>
            </span>
          ) : null}
        </div>
        {searchPrice != null && personas.length > 0 ? (
          <FindPeopleButton
            accountId={accountId}
            accountName={accountName}
            personas={personas}
            price={searchPrice}
            searchedAt={data.people_searched_at ?? null}
          />
        ) : null}
      </div>

      {contacts.length === 0 ? (
        <EmptyTabState
          message={needle ? `No one matches "${query.trim()}"` : 'No related contacts'}
        />
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table
            containerClassName={cn(scrollable && 'max-h-[28rem]')}
            className="table-fixed [&_td:not(.sticky)]:overflow-hidden [&_td:not(.sticky)]:text-ellipsis [&_td]:whitespace-nowrap [&_th]:whitespace-nowrap"
            style={{ minWidth: TABLE_MIN_WIDTH }}
          >
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column.id}
                    style={
                      getPinnedColumnStyle(PINNED, column.id) ?? {
                        width: column.width,
                        minWidth: column.width,
                      }
                    }
                    className={
                      getPinnedHeaderClassName(PINNED, column.id) ??
                      'sticky top-0 z-10 bg-background'
                    }
                  >
                    {column.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts.map((contact) => (
                <AccountContactRow
                  key={contact.id}
                  contact={contact}
                  accountWebsite={accountWebsite}
                  onOpenContact={onOpenContact}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function AccountContactRow({
  contact,
  accountWebsite,
  onOpenContact,
}: {
  contact: ApiContactSummary;
  accountWebsite: string | null;
  onOpenContact: (contactId: string) => void;
}) {
  const linkedin = normalizeExternalUrl(contact.linkedin_url);
  return (
    <TableRow
      data-testid="account-contact-row"
      data-contact-id={contact.id}
      className="group/row h-12 cursor-pointer"
      onClick={() => onOpenContact(contact.id)}
    >
      <TableCell
        style={getPinnedColumnStyle(PINNED, 'name')}
        className={getPinnedColumnClassName(PINNED, 'name')}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <ContactAvatar
            name={contact.name}
            accountWebsite={contact.account_website ?? accountWebsite}
            email={contact.email}
            size="sm"
          />
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpenContact(contact.id);
            }}
            className="min-w-0 flex-1 truncate text-left font-medium text-foreground hover:underline"
          >
            {contact.name}
          </button>
          {linkedin ? (
            <a
              href={linkedin}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => event.stopPropagation()}
              aria-label={`${contact.name} on LinkedIn`}
              className="inline-flex shrink-0 opacity-50 grayscale transition hover:opacity-100 hover:grayscale-0"
            >
              <AppLogo name="linkedin" size={14} title="LinkedIn" />
            </a>
          ) : null}
        </div>
      </TableCell>
      <TableCell>
        {contact.reach?.persona ? (
          <PersonaTag family={contact.reach.persona} match={contact.reach.persona_match} />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">{contact.title ?? '—'}</TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        <EmailCell contact={contact} variant="table" />
      </TableCell>
      <TableCell onClick={(event) => event.stopPropagation()}>
        <PhoneCell contact={contact} />
      </TableCell>
    </TableRow>
  );
}
